import { getObjectAtSystemDefinitionSlot, getComponentType } from '../../project-definition-data-access';
import {
  removeAccessory as defRemoveAccessory,
  addAccessory as defAddAccessory,
} from '../defaults';

export function getSolarProductInfoBySlotName(slotName, systemDefinition, systemProducts) {
  // Note: most products are under 'sets', solar kits are under 'solar'
  const solarKitData = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!solarKitData) {
    return null;
  }
  const solarKitId = solarKitData.id;
  const solarArray = systemProducts.attributes.solar;
  if (!solarArray) {
    return null;
  }
  const { property } = solarKitData;
  const kit = property ? property.kit : null;

  for (let i = 0, length = solarArray.length; i < length; i += 1) {
    // item is a set of pipings & a solar kit, not the solar kit itself,
    // so look for id in solar-kit.id
    const item = solarArray[i];
    const itemSolarData = item['solar-kit'];
    if (
      itemSolarData.id === solarKitId &&
      itemSolarData.set_id === kit
    ) {
      return item;
    }
  }
  return null;
}

export function getSystemObjectPropertyValue(slotName, systemDefinition) {
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!data) {
    return null;
  }
  const prop = data.property;
  if (prop && typeof (prop) === 'object') {
    return prop.piping;
  }
  return prop;
}

export function setSystemObjectPropertyValue(slotName, value, systemDefinition, systemProducts) {
  const data = getObjectAtSystemDefinitionSlot(slotName, systemDefinition);
  if (!data) {
    return;
  }
  if (!data.property || typeof (data.property) !== 'object') {
    data.property = {};
  }
  data.property.piping = value;

  const optionsName = `piping-${value}`;

  const solarKitProductData = getSolarProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  const option = solarKitProductData[optionsName];

  // Remove piping accessories
  defRemoveAccessory(slotName, solarKitProductData['piping-10'], systemDefinition, systemProducts);
  defRemoveAccessory(slotName, solarKitProductData['piping-15'], systemDefinition, systemProducts);

  // Add required accessory (piping-10 or 15)
  // option.required property should always be true
  defAddAccessory(slotName, option, systemDefinition, systemProducts);
}

export function getProductInfoBySlotName(slotName, systemDefinition, systemProducts) {
  const info = getSolarProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  if (!info) {
    return null;
  }
  return info['solar-kit'];
}

export function replaceProductInSystemDefinition(slotName, systemDefinition, systemProducts, product) { // eslint-disable-line
  // Current solar item in the system definition
  const currentData = getObjectAtSystemDefinitionSlot(slotName, systemDefinition) || {};

  const sysDef = systemDefinition;
  const slots = sysDef.attributes.slots || {};
  sysDef.attributes.slots = slots;

  currentData.id = product.id;
  // Very important to assign the amount
  currentData.amount = product.amount;
  const { properties } = product;
  let property = null;
  if (properties) {
    // Set default solar property (=10) if it's available
    if (properties.indexOf('10') >= 0 || properties.indexOf(10) >= 0) {
      property = 10;
    } else {
      // Default property of 10 not available for some reason?
      // In that case we just pick the first one
      property = properties[0];
    }
  }
  currentData.property = property;
  currentData.options = [];
  slots[slotName] = currentData;
}

export function addProductToSystemDefinition(product, systemDefinition, systemProducts) { // eslint-disable-line
  // In this case, we can reuse code from 'replaceProductInSystemDefinition'
  const componentType = getComponentType('solar-kit');

  // componentType.slot is probably already 'solar-kit', but this way its easier to solve problems in case the slot name suddenly changes in the BE API
  replaceProductInSystemDefinition(componentType.slot, product, systemDefinition, systemProducts);
}

export function getSystemObjectProperties(slotName, systemDefinition, systemProducts) {
  if (!slotName || !systemDefinition || !systemProducts) {
    // #if DEBUG
    console.warn('getSystemObjectProperties - Invalid parameters:', { slotName, systemDefinition, systemProducts });
    // #endif
    return null;
  }
  const slotType = getComponentType(slotName);
  const slotID = slotType ? slotType.slot : null;

  // Some unusual situations
  const solarKitProductData = getSolarProductInfoBySlotName(slotID, systemDefinition, systemProducts);
  const solarKit = solarKitProductData ? solarKitProductData['solar-kit'] : null;
  const properties = solarKit ? solarKit.properties : null;
  if (!properties) {
    return [];
  }
  return properties.map((property) => {
    return {
      labelKey: `configurator.productTypes.solarKit.piping${property}`,
      value: property,
    };
  });
}

export function getSystemObjectAccessories(slotName, systemDefinition, systemProducts, productData, onlyOptionalAccessories) {
  const solarData = systemDefinition.attributes.slots[slotName];
  const solarKitProductData = getSolarProductInfoBySlotName(slotName, systemDefinition, systemProducts);
  if (!solarKitProductData) {
    return null;
  }

  const solarKit = solarKitProductData['solar-kit'];
  let solarKitAccessories = null;
  if (solarKit) {
    solarKitAccessories = onlyOptionalAccessories ?
      solarKit.optional_accessories || solarKit.accessory_collections
      : solarKit.accessories;
  }

  let resAccessories = solarKitAccessories;
  if (onlyOptionalAccessories) {
    return resAccessories ? resAccessories.concat() : null;
  }
  const solarDataProp = solarData ? solarData.property : null;
  if (solarDataProp) {
    const solarDataPropName = typeof (solarDataProp) === 'object' ? solarDataProp.piping : solarDataProp;
    const pipingAccessory = solarKitProductData[`piping-${solarDataPropName}`];
    if (pipingAccessory) {
      resAccessories = resAccessories ? resAccessories.concat() : [];
      resAccessories.push(pipingAccessory);
    }
  }
  return resAccessories;
}



// WEBPACK FOOTER //
// ./js/utils/project-definition/component/solar-kit/index.js